import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { PublicHeader, PublicFooter, ArticleCard } from '../components/PublicLayout';
import { articlesAPI, publicAPI, commentsAPI, analyticsAPI, shareAPI, schemaAPI, vitalsAPI, ttsAPI, vizAPI, ga4API, blocksAPI } from '../lib/api';
import { applyArticleSEO, resetPublicSEO, syndicationSource } from '../lib/article-meta';
import BlockRenderer from '../components/BlockRenderer';
import PaywallGate from '../components/PaywallGate';
import { useAuth } from '../lib/auth-context';
import { Skeleton } from '../components/ui/skeleton';
import { Separator } from '../components/ui/separator';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Calendar, Clock, Eye, Share2, ThumbsUp, ThumbsDown, Flag, MessageSquare, Mail, Link2, Zap, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import Markdown from 'react-markdown';
import { formatDisplayViews, getDisplayViews } from '../lib/view-count';

export default function ArticlePage() {
  const { id: routeArticleId, slug: routeSlug } = useParams();
  const routeKey = routeArticleId || routeSlug;
  const { user } = useAuth();
  const [article, setArticle] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [shareData, setShareData] = useState(null);
  const [showShareBar, setShowShareBar] = useState(false);
  const [readProgress, setReadProgress] = useState(0);
  const [ttsAudio, setTtsAudio] = useState(null);
  const [ttsLoading, setTtsLoading] = useState(false);
  const [vizList, setVizList] = useState([]);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [blocks, setBlocks] = useState([]);

  const handleScroll = useCallback(() => {
    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    const progress = docHeight > 0 ? Math.min(100, (scrollTop / docHeight) * 100) : 0;
    setReadProgress(progress);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    loadArticle();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeArticleId, routeSlug]);

  useEffect(() => {
    if (!routeKey) return undefined;
    if (loading) return undefined;
    if (!article) {
      resetPublicSEO();
      return undefined;
    }
    const canonical = routeSlug
      ? `${window.location.origin}/p/${encodeURIComponent(routeSlug)}`
      : `${window.location.origin}/article/${article.id}`;
    applyArticleSEO(article, { url: canonical });
    return () => resetPublicSEO();
  }, [routeKey, routeSlug, loading, article]);

  const loadArticle = async () => {
    if (!routeKey) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = routeSlug
        ? await articlesAPI.getBySlug(routeSlug)
        : await articlesAPI.get(routeArticleId);
      const data = res.data;
      const aid = data.id;
      setArticle(data);
      analyticsAPI.recordEvent({ event_type: 'pageview', article_id: aid }).catch(() => {});
      // GA4 server-side measurement protocol
      ga4API.pageview({ page_title: data.title, url: window.location.href, article_id: aid }).catch(() => {});
      // Record Core Web Vitals using PerformanceObserver (non-blocking)
      try {
        if (typeof window !== 'undefined' && 'PerformanceObserver' in window) {
          const nav = performance.getEntriesByType('navigation')[0];
          const vitalsDoc = { url: window.location.href, article_id: aid, device_type: /mobile|android|iphone/i.test(navigator.userAgent) ? 'mobile' : 'desktop' };
          if (nav) { vitalsDoc.ttfb = Math.round(nav.responseStart - nav.requestStart); }
          // LCP
          new PerformanceObserver(list => {
            const last = list.getEntries().at(-1);
            if (last) vitalsAPI.record({ ...vitalsDoc, lcp: Math.round(last.startTime) }).catch(() => {});
          }).observe({ type: 'largest-contentful-paint', buffered: true });
          // CLS
          let cls = 0;
          new PerformanceObserver(list => {
            list.getEntries().forEach(e => { if (!e.hadRecentInput) cls += e.value; });
          }).observe({ type: 'layout-shift', buffered: true });
          // Report CLS + TTFB on unload
          window.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'hidden') {
              vitalsAPI.record({ ...vitalsDoc, cls: Math.round(cls * 1000) / 1000, ttfb: vitalsDoc.ttfb }).catch(() => {});
            }
          }, { once: true });
        }
      } catch (_) {}
      const commRes = await commentsAPI.list(aid);
      setComments(commRes.data.comments || []);
      shareAPI.getData(aid).then(r => setShareData(r.data)).catch(() => {});
      vizAPI.forArticle(aid).then(r => setVizList(r.data.visualizations || [])).catch(() => {});
      // Load structured blocks if the article has them
      if (data.has_blocks || (Array.isArray(data.blocks) && data.blocks.length)) {
        if (Array.isArray(data.blocks) && data.blocks.length) {
          setBlocks(data.blocks);
        } else {
          blocksAPI.get(aid).then(r => setBlocks(r.data.blocks || [])).catch(() => {});
        }
      } else {
        setBlocks([]);
      }
      schemaAPI.article(aid).then(r => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.id = 'article-jsonld';
        script.textContent = JSON.stringify(r.data);
        // Remove existing if re-rendering
        const existing = document.getElementById('article-jsonld');
        if (existing) existing.remove();
        document.head.appendChild(script);
      }).catch(() => {});
    } catch (err) {
      console.error('Failed to load article:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    const aid = article?.id;
    if (!newComment.trim() || !user || !aid) return;
    try {
      await commentsAPI.create(aid, { article_id: aid, content: newComment });
      setNewComment('');
      const commRes = await commentsAPI.list(aid);
      setComments(commRes.data.comments || []);
      toast.success('Comment posted!');
    } catch {
      toast.error('Failed to post comment');
    }
  };

  const handleLike = async (commentId) => {
    const aid = article?.id;
    if (!aid) return;
    try {
      await commentsAPI.like(commentId);
      const commRes = await commentsAPI.list(aid);
      setComments(commRes.data.comments || []);
    } catch {}
  };

  const handleDislike = async (commentId) => {
    const aid = article?.id;
    if (!aid) return;
    try {
      await commentsAPI.dislike(commentId);
      const commRes = await commentsAPI.list(aid);
      setComments(commRes.data.comments || []);
    } catch {}
  };

  const handleReport = async (commentId) => {
    try {
      await commentsAPI.report(commentId, 'inappropriate');
      toast.success('Comment reported');
    } catch {
      toast.error('Failed to report');
    }
  };

  const handleShare = () => {
    setShowShareBar(!showShareBar);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Link copied to clipboard!');
  };

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareTitle = article?.title || 'Neural News';
  const apiOrigin = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
  const ampHref =
    article?.id &&
    `${apiOrigin || (typeof window !== 'undefined' ? window.location.origin : '')}/api/public/amp/article/${article.id}`;

  const buildShareTargets = () => {
    const u = encodeURIComponent(shareUrl);
    const t = encodeURIComponent(shareTitle);
    const fromApi = shareData?.platforms || {};
    return {
      twitter: fromApi.twitter || `https://twitter.com/intent/tweet?text=${t}&url=${u}`,
      facebook: fromApi.facebook || `https://www.facebook.com/sharer/sharer.php?u=${u}`,
      linkedin: fromApi.linkedin || `https://www.linkedin.com/sharing/share-offsite/?url=${u}`,
      email: fromApi.email || `mailto:?subject=${t}&body=${u}`,
    };
  };

  const handleNativeShare = async () => {
    if (!navigator.share) {
      copyLink();
      return;
    }
    try {
      await navigator.share({
        title: shareTitle,
        url: shareUrl,
        text: article?.subtitle || shareTitle,
      });
    } catch (e) {
      if (e?.name !== 'AbortError') copyLink();
    }
  };

  if (loading) {
    return (
      <>
        <PublicHeader />
        <main className="max-w-[800px] mx-auto px-4 sm:px-6 py-8">
          <Skeleton className="h-8 w-3/4 mb-4" />
          <Skeleton className="h-4 w-1/2 mb-8" />
          <Skeleton className="h-96 rounded-xl mb-8" />
          <Skeleton className="h-4 w-full mb-2" />
          <Skeleton className="h-4 w-full mb-2" />
          <Skeleton className="h-4 w-3/4" />
        </main>
      </>
    );
  }

  if (!article) {
    return (
      <>
        <PublicHeader />
        <main className="max-w-[800px] mx-auto px-4 sm:px-6 py-16 text-center">
          <h1 className="text-2xl font-bold">Article Not Found</h1>
          <Link to="/" className="text-[#1E5A8A] mt-4 inline-block">Back to Home</Link>
        </main>
        <PublicFooter />
      </>
    );
  }

  const readingTime = Math.max(1, Math.ceil((article.content?.split(/\s+/).length || 0) / 200));
  // Same helper the canonical link uses, so the visible credit and the
  // <link rel="canonical"> can never point at different publishers.
  const syndication = syndicationSource(article);
  const displayByline = syndication
    ? (syndication.author ? `${syndication.author} (${syndication.publication})` : syndication.publication)
    : (article.author?.full_name || article.author_name || 'Neural News');

  return (
    <>
      <PublicHeader />
      {/* Reading Progress Bar */}
      <div className="fixed top-0 left-0 right-0 z-[60] h-1 bg-transparent">
        <div className="h-full bg-[#B89B5E] transition-all duration-150" style={{ width: `${readProgress}%` }} />
      </div>
      <main className="bg-[#FBF7EF] min-h-screen">
        <article className="max-w-[800px] mx-auto px-4 sm:px-6 py-8">
          {/* Category + Tags */}
          <div className="flex items-center gap-2 mb-4">
            {article.category && (
              <Link to={`/category/${article.category.slug}`}>
                <Badge variant="secondary" className="news-kicker text-[10px]" style={{ color: article.category.color }}>
                  {article.category.name}
                </Badge>
              </Link>
            )}
          </div>

          {/* Title */}
          <h1 data-testid="article-title" className="news-masthead text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight tracking-tight">
            {article.title}
          </h1>

          {/* Subtitle */}
          {article.subtitle && (
            <p className="text-lg text-[#5B616E] mt-3 leading-relaxed">{article.subtitle}</p>
          )}

          {/* Byline */}
          <div className="flex items-center justify-between mt-6 py-4 border-y border-[#E7E0D6]">
            <div className="flex items-center gap-4">
              {article.author?.avatar_url ? (
                <img src={article.author.avatar_url} alt="" className="w-10 h-10 rounded-full object-cover" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-[#E7E0D6] flex items-center justify-center text-sm font-semibold">
                  {article.author?.full_name?.charAt(0) || 'N'}
                </div>
              )}
              <div>
                <p className="font-medium text-sm">{displayByline}</p>
                <div className="flex items-center gap-3 text-xs text-[#5B616E]">
                  {syndication && <span className="font-medium uppercase tracking-wide text-[10px]">Syndicated</span>}
                  <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(article.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                  {!syndication && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{readingTime} min read</span>}
                  <span className="flex items-center gap-1" title={`${getDisplayViews(article.views, article.id).toLocaleString()} views`}><Eye className="w-3 h-3" />{formatDisplayViews(article.views, article.id)} views</span>
                </div>
              </div>
            </div>
            <Button variant="ghost" size="sm" data-testid="article-share-button" onClick={handleShare}>
              <Share2 className="w-4 h-4" />
            </Button>
          </div>

          {/* Social Share Bar — works with or without share API; adds Web Share on supported devices */}
          {showShareBar && (() => {
            const p = buildShareTargets();
            return (
              <div className="flex flex-wrap items-center gap-2 py-3 px-4 bg-white rounded-xl border border-[#E7E0D6] mt-3 animate-in fade-in slide-in-from-top-2">
                <span className="text-xs text-[#5B616E] font-medium mr-1">Share:</span>
                {typeof navigator !== 'undefined' && navigator.share && (
                  <button
                    type="button"
                    onClick={handleNativeShare}
                    className="text-xs font-semibold px-3 py-2 rounded-lg bg-[#121316] text-white hover:bg-[#1B1D22] transition-colors"
                    data-testid="article-native-share"
                  >
                    Share…
                  </button>
                )}
                <a href={p.twitter} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-[#1DA1F2]/10 hover:bg-[#1DA1F2]/20 flex items-center justify-center transition-colors" title="Share on X" aria-label="Share on X">
                  <svg className="w-4 h-4 text-[#1DA1F2]" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                </a>
                <a href={p.facebook} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-[#1877F2]/10 hover:bg-[#1877F2]/20 flex items-center justify-center transition-colors" title="Share on Facebook" aria-label="Share on Facebook">
                  <svg className="w-4 h-4 text-[#1877F2]" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                </a>
                <a href={p.linkedin} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-[#0A66C2]/10 hover:bg-[#0A66C2]/20 flex items-center justify-center transition-colors" title="Share on LinkedIn" aria-label="Share on LinkedIn">
                  <svg className="w-4 h-4 text-[#0A66C2]" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                </a>
                <a href={p.email} className="w-9 h-9 rounded-lg bg-[#5B616E]/10 hover:bg-[#5B616E]/20 flex items-center justify-center transition-colors" title="Share via Email" aria-label="Share via Email">
                  <Mail className="w-4 h-4 text-[#5B616E]" />
                </a>
                {ampHref && (
                  <a
                    href={ampHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 rounded-lg bg-amber-500/15 hover:bg-amber-500/25 flex items-center justify-center transition-colors"
                    title="Open AMP version (fast mobile / syndication)"
                    aria-label="AMP version"
                    data-testid="article-amp-link"
                  >
                    <Zap className="w-4 h-4 text-amber-700 dark:text-amber-400" />
                  </a>
                )}
                <button type="button" onClick={copyLink} className="w-9 h-9 rounded-lg bg-[#B89B5E]/10 hover:bg-[#B89B5E]/20 flex items-center justify-center transition-colors" title="Copy Link" aria-label="Copy link">
                  <Link2 className="w-4 h-4 text-[#B89B5E]" />
                </button>
              </div>
            );
          })()}

          {/* Hero Image */}
          {article.featured_image && (
            <div className="mt-6 rounded-xl overflow-hidden aspect-[16/9]">
              <img src={article.featured_image} alt={article.title} className="w-full h-full object-cover" />
            </div>
          )}

          {/* Video — HLS or embed */}
          {article.video_url && (
            <div className="mt-6 rounded-xl overflow-hidden">
              {article.video_url.endsWith('.m3u8') ? (
                <video controls className="w-full rounded-xl" style={{ maxHeight: 400 }} crossOrigin="anonymous">
                  <source src={article.video_url} type="application/x-mpegURL" />
                  Your browser does not support HLS video.
                </video>
              ) : (
                <div className="aspect-video">
                  <iframe src={article.video_url} title="Video" className="w-full h-full" allowFullScreen />
                </div>
              )}
            </div>
          )}

          {/* Text-to-Speech */}
          {article.id && (
            <div className="mt-4 flex items-center gap-2">
              {!ttsAudio ? (
                <button
                  onClick={async () => {
                    setTtsLoading(true);
                    try {
                      const r = await ttsAPI.generate(article.id, 'alloy');
                      if (r.data.audio_base64) {
                        const src = `data:audio/mpeg;base64,${r.data.audio_base64}`;
                        setTtsAudio(src);
                      } else {
                        toast.info(r.data.message || 'TTS not available');
                      }
                    } catch { toast.error('TTS failed'); }
                    finally { setTtsLoading(false); }
                  }}
                  disabled={ttsLoading}
                  className="flex items-center gap-1.5 text-xs text-[#5B616E] hover:text-[#121316] border border-[#E7E0D6] rounded-lg px-3 py-1.5 transition-colors bg-white"
                >
                  🔊 {ttsLoading ? 'Generating…' : 'Listen to this article'}
                </button>
              ) : (
                <div className="w-full">
                  <p className="text-xs text-[#5B616E] mb-1">🔊 Article audio</p>
                  <audio controls src={ttsAudio} className="w-full h-10" />
                </div>
              )}
            </div>
          )}

          {/* Content — block renderer when structured, otherwise markdown/HTML */}
          <PaywallGate articleId={article.id} isPremiumArticle={!!article.is_premium}>
            <div data-testid="article-body" className="article-content prose-news mt-8">
              {blocks && blocks.length > 0 ? (
                <BlockRenderer blocks={blocks} />
              ) : article.content?.startsWith('<') ? (
                <div dangerouslySetInnerHTML={{ __html: article.content }} />
              ) : (
                <Markdown>{article.content || ''}</Markdown>
              )}
            </div>
          </PaywallGate>

          {/* Outbound link to the original. Rendered OUTSIDE PaywallGate:
              our snippet is a pointer to someone else's reporting, so we
              must never put a paywall between the reader and the source. */}
          {syndication && (
            <aside
              data-testid="article-source-link"
              className="mt-8 rounded-xl border border-[#B89B5E]/50 bg-white p-5"
            >
              <p className="text-xs font-semibold text-[#5B616E]">
                {syndication.author
                  ? `Reported by ${syndication.author} for ${syndication.publication}`
                  : `Reported by ${syndication.publication}`}
              </p>
              <p className="mt-1 text-sm text-[#5B616E]">
                You are reading a short summary. The full article is published by {syndication.publication}.
              </p>
              <a
                href={syndication.url}
                target="_blank"
                rel="noopener nofollow"
                className="mt-4 inline-flex items-center gap-2 rounded-lg bg-[#B89B5E] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#A2874E]"
              >
                <ExternalLink className="h-4 w-4" />
                Read the full story at {syndication.publication}
              </a>
            </aside>
          )}

          {/* Gallery view for gallery story_type */}
          {article.story_type === 'gallery' && article.blocks && (
            <div className="mt-6">
              {(() => {
                const galleryBlocks = article.blocks.filter(b => b.type === 'gallery' || b.type === 'image');
                const allImages = galleryBlocks.flatMap(b =>
                  b.type === 'gallery' ? (b.images || []) : [{ url: b.url, caption: b.caption }]
                ).filter(i => i.url);
                if (!allImages.length) return null;
                const current = allImages[galleryIndex] || allImages[0];
                return (
                  <div className="rounded-2xl overflow-hidden bg-black relative">
                    <img
                      src={current.url}
                      alt={current.caption || ''}
                      className="w-full object-cover transition-all duration-500"
                      style={{ maxHeight: 520 }}
                    />
                    {current.caption && (
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                        <p className="text-white text-sm">{current.caption}</p>
                      </div>
                    )}
                    {allImages.length > 1 && (
                      <>
                        <button onClick={() => setGalleryIndex(i => (i - 1 + allImages.length) % allImages.length)}
                          className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center text-lg transition-colors">‹</button>
                        <button onClick={() => setGalleryIndex(i => (i + 1) % allImages.length)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center text-lg transition-colors">›</button>
                        <div className="absolute bottom-14 left-0 right-0 flex justify-center gap-1.5">
                          {allImages.map((_, i) => (
                            <button key={i} onClick={() => setGalleryIndex(i)}
                              className={`w-2 h-2 rounded-full transition-colors ${i === galleryIndex ? 'bg-white' : 'bg-white/40'}`} />
                          ))}
                        </div>
                      </>
                    )}
                    <div className="absolute top-3 right-3 bg-black/50 text-white text-xs px-2 py-1 rounded-full">
                      {galleryIndex + 1} / {allImages.length}
                    </div>
                  </div>
                );
              })()}
              <div className="grid grid-cols-5 gap-1 mt-2">
                {article.blocks?.flatMap(b => b.type === 'gallery' ? (b.images || []) : b.type === 'image' ? [{ url: b.url }] : [])
                  .filter(i => i.url).slice(0, 10).map((img, i) => (
                  <img key={i} src={img.url} alt="" onClick={() => setGalleryIndex(i)}
                    className={`w-full aspect-square object-cover rounded cursor-pointer transition-all ${i === galleryIndex ? 'ring-2 ring-[#B89B5E]' : 'opacity-70 hover:opacity-100'}`} />
                ))}
              </div>
            </div>
          )}

          {/* Data Visualizations */}
          {vizList.length > 0 && (
            <div className="mt-8 space-y-4">
              {vizList.map(viz => (
                <div key={viz.id} className="border border-[#E7E0D6] rounded-xl p-4 bg-white">
                  <p className="text-sm font-semibold mb-3">{viz.title}</p>
                  <div className="text-xs text-[#5B616E] bg-[#FBF7EF] rounded-lg p-3">
                    <p>📊 Interactive {viz.type} chart — {(viz.data?.rows || []).length} data points</p>
                    <p className="mt-1">Chart type: {viz.type}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Tags */}
          {article.tags?.length > 0 && (
            <div className="mt-8 flex flex-wrap gap-2">
              {article.tags.map(tag => (
                <Badge key={tag} variant="secondary" className="text-xs">{tag}</Badge>
              ))}
            </div>
          )}

          <Separator className="my-10" />

          {/* Comments */}
          <section data-testid="article-comments" className="mt-8">
            <h3 className="news-masthead text-xl font-bold flex items-center gap-2 mb-6">
              <MessageSquare className="w-5 h-5" />
              Comments ({comments.length})
            </h3>

            {/* Comment Form */}
            {user ? (
              <form onSubmit={handleComment} className="mb-8">
                <textarea
                  data-testid="comment-form-textarea"
                  value={newComment}
                  onChange={e => setNewComment(e.target.value)}
                  placeholder="Share your thoughts..."
                  className="w-full border border-[#E7E0D6] rounded-xl p-4 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#B89B5E] resize-none"
                  rows={3}
                  required
                />
                <div className="flex justify-end mt-2">
                  <Button data-testid="comment-form-submit-button" type="submit" className="bg-[#121316] text-white hover:bg-[#1B1D22]">
                    Post Comment
                  </Button>
                </div>
              </form>
            ) : (
              <div className="mb-8 p-4 bg-white rounded-xl border border-[#E7E0D6] text-center">
                <p className="text-sm text-[#5B616E]">Please <Link to="/login" className="text-[#1E5A8A] underline">sign in</Link> to comment.</p>
              </div>
            )}

            {/* Comments List */}
            <div className="space-y-4">
              {comments.map(comment => (
                <div key={comment.id} data-testid="comment-item" className="bg-white rounded-xl border border-[#E7E0D6] p-4">
                  <div className="flex items-center gap-3 mb-2">
                    {(comment.avatar_url || comment.author?.avatar_url) ? (
                      <img src={comment.avatar_url || comment.author?.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-[#E7E0D6] flex items-center justify-center text-xs font-semibold">
                        {comment.author_name?.charAt(0) || 'U'}
                      </div>
                    )}
                    <div>
                      <p className="text-sm font-medium">{comment.author_name || 'Anonymous'}</p>
                      <p className="text-xs text-[#5B616E]">{new Date(comment.created_at).toLocaleString()}</p>
                    </div>
                  </div>
                  <p className="text-sm leading-relaxed ml-11">{comment.content}</p>
                  <div className="flex items-center gap-4 ml-11 mt-3">
                    <button onClick={() => handleLike(comment.id)} className="flex items-center gap-1 text-xs text-[#5B616E] hover:text-[#121316]">
                      <ThumbsUp className="w-3.5 h-3.5" /> {comment.likes || 0}
                    </button>
                    <button onClick={() => handleDislike(comment.id)} className="flex items-center gap-1 text-xs text-[#5B616E] hover:text-[#121316]">
                      <ThumbsDown className="w-3.5 h-3.5" /> {comment.dislikes || 0}
                    </button>
                    <button data-testid="comment-report-button" onClick={() => handleReport(comment.id)} className="flex items-center gap-1 text-xs text-[#5B616E] hover:text-[#B3261E]">
                      <Flag className="w-3.5 h-3.5" /> Report
                    </button>
                  </div>
                </div>
              ))}
              {comments.length === 0 && (
                <p className="text-sm text-[#5B616E] text-center py-6">No comments yet. Be the first to share your thoughts!</p>
              )}
            </div>
          </section>

          {/* Related Articles */}
          {article.related_articles?.length > 0 && (
            <section className="mt-12">
              <Separator className="mb-8" />
              <h3 className="news-masthead text-xl font-bold mb-6">Related Stories</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {article.related_articles.slice(0, 4).map(a => (
                  <ArticleCard key={a.id} article={a} variant="compact" />
                ))}
              </div>
            </section>
          )}
        </article>
      </main>
      <PublicFooter />
    </>
  );
}
